<script setup lang="ts">
import { Unlink } from "@lucide/vue";
import { useI18n } from "vue-i18n";

import { MasterServiceForm } from "@/entities/master-service";
import { Avatar, Button } from "@/shared/ui";
import { getInitials } from "@/shared/ui/avatar";

withDefaults(
  defineProps<{
    masterName: string;
    serviceName: string;
    masterId: string;
    config: {
      duration: string;
      price: string;
      buffer: string;
      status: "idle" | "saving";
      fieldErrors: Partial<Record<"duration" | "price" | "buffer", string>>;
      formError?: string;
    };
    canDetach?: boolean;
  }>(),
  { canDetach: false },
);

const emit = defineEmits<{
  save: [];
  cancel: [];
  detach: [];
  "update:duration": [value: string];
  "update:price": [value: string];
  "update:buffer": [value: string];
}>();

const { t } = useI18n();
</script>

<template>
  <div class="service-master-config-step">
    <MasterServiceForm
      mode="edit"
      footer-layout="stacked"
      :service-id="null"
      :available="[]"
      :duration="config.duration"
      :price="config.price"
      :buffer="config.buffer"
      :status="config.status"
      :field-errors="config.fieldErrors"
      :form-error="config.formError"
      @update:duration="emit('update:duration', $event)"
      @update:price="emit('update:price', $event)"
      @update:buffer="emit('update:buffer', $event)"
      @submit="emit('save')"
      @cancel="emit('cancel')"
    >
      <template #context>
        <div class="service-master-config-step__context">
          <Avatar size="sm">
            {{ getInitials(masterName) }}
          </Avatar>
          <span
            class="service-master-config-step__context-label text-sm font-medium"
            data-testid="config-master-name"
          >{{ masterName }} · {{ serviceName }}</span>
        </div>
      </template>

      <template #after-fields>
        <div
          v-if="canDetach"
          class="service-master-config-step__danger-zone"
          data-testid="danger-zone"
        >
          <p
            class="service-master-config-step__danger-label"
            data-testid="danger-label"
          >
            {{ t("services.detach.dangerLabel") }}
          </p>
          <Button
            variant="ghost-destructive"
            class="w-full"
            data-testid="detach-btn"
            @click="emit('detach')"
          >
            <Unlink aria-hidden="true" />
            {{ t("services.detach.trigger") }}
          </Button>
          <p
            class="service-master-config-step__danger-hint"
            data-testid="detach-hint"
          >
            {{ t("services.detach.hint") }}
          </p>
        </div>
      </template>
    </MasterServiceForm>
  </div>
</template>

<style scoped>
.service-master-config-step {
  display: flex;
  flex-direction: column;
}

.service-master-config-step__context {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.service-master-config-step__danger-zone {
  border-top: 1px solid var(--border);
  padding-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.service-master-config-step__danger-label {
  font-size: 0.6875rem;
  font-weight: 600;
  color: var(--muted-foreground);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.service-master-config-step__danger-hint {
  font-size: 0.75rem;
  color: var(--muted-foreground);
  line-height: 1.5;
}
</style>
